<script lang="ts">
  import { createEventDispatcher, onMount, tick } from 'svelte';
  import { portal } from '../lib/actions/portal';
  import type { SelectOption } from '../lib/types/select-field';

  export let id: string;
  export let value = '';
  export let options: SelectOption[] = [];
  export let placeholder = 'Select an option';
  export let disabled = false;
  export let invalid = false;
  export let ariaDescribedby: string | undefined = undefined;

  const dispatch = createEventDispatcher<{ change: string }>();

  let isOpen = false;
  let containerRef: HTMLDivElement;
  let triggerRef: HTMLButtonElement;
  let menuRef: HTMLUListElement;
  let highlightedIndex = -1;
  let menuStyle = '';
  let openUpward = false;

  $: selectedOption = options.find((option) => option.value === value);
  $: displayLabel = selectedOption?.label ?? placeholder;
  $: hasValue = value !== '';

  function buildMenuStyle(rect: DOMRect, upward: boolean) {
    const viewportPadding = 8;
    const gap = 6;
    const preferredMaxHeight = 256;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const maxHeight = Math.max(
      120,
      Math.min(preferredMaxHeight, upward ? spaceAbove - gap : spaceBelow - gap)
    );

    if (upward) {
      const top = Math.max(viewportPadding, rect.top - maxHeight - gap);
      const height = rect.top - gap - top;
      return [
        'position: fixed',
        `top: ${top}px`,
        `left: ${rect.left}px`,
        `width: ${rect.width}px`,
        `max-height: ${height}px`,
        'z-index: 9999',
      ].join('; ');
    }

    return [
      'position: fixed',
      `top: ${rect.bottom + gap}px`,
      `left: ${rect.left}px`,
      `width: ${rect.width}px`,
      `max-height: ${maxHeight}px`,
      'z-index: 9999',
    ].join('; ');
  }

  async function updateMenuPosition() {
    if (!triggerRef) return;

    await tick();

    const rect = triggerRef.getBoundingClientRect();
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;

    openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    menuStyle = buildMenuStyle(rect, openUpward);
  }

  async function open() {
    if (disabled) return;
    isOpen = true;
    highlightedIndex = options.findIndex((option) => option.value === value);
    await tick();
    await updateMenuPosition();
  }

  function close() {
    isOpen = false;
    highlightedIndex = -1;
    menuStyle = '';
  }

  function selectOption(option: SelectOption) {
    value = option.value;
    close();
    dispatch('change', option.value);
    triggerRef?.focus();
  }

  function handleTriggerClick() {
    if (isOpen) {
      close();
    } else {
      void open();
    }
  }

  function handleTriggerKeydown(event: KeyboardEvent) {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          void open();
        } else {
          highlightedIndex = Math.min(highlightedIndex + 1, options.length - 1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          void open();
        } else {
          highlightedIndex = Math.max(highlightedIndex - 1, 0);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          void open();
        } else if (highlightedIndex >= 0) {
          selectOption(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
    }
  }

  function handleClickOutside(event: MouseEvent) {
    const target = event.target as Node;
    if (containerRef?.contains(target) || menuRef?.contains(target)) {
      return;
    }
    close();
  }

  function handleViewportChange() {
    if (isOpen) {
      void updateMenuPosition();
    }
  }

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  });
</script>

<div class="select-field" bind:this={containerRef}>
  <button
    bind:this={triggerRef}
    type="button"
    {id}
    class="select-field-trigger"
    class:open={isOpen}
    class:invalid
    class:placeholder={!hasValue}
    {disabled}
    aria-haspopup="listbox"
    aria-expanded={isOpen}
    aria-describedby={ariaDescribedby}
    on:click={handleTriggerClick}
    on:keydown={handleTriggerKeydown}
  >
    <span class="select-field-value">{displayLabel}</span>
    <span class="select-field-chevron" class:open={isOpen} aria-hidden="true">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M7 10l5 5 5-5z" />
      </svg>
    </span>
  </button>
</div>

{#if isOpen}
  <ul
    bind:this={menuRef}
    use:portal={'body'}
    class="select-field-options"
    class:open-upward={openUpward}
    style={menuStyle}
    role="listbox"
    aria-labelledby={id}
  >
    {#each options as option, index (option.value)}
      <li role="presentation">
        <button
          type="button"
          role="option"
          class="select-field-option"
          class:selected={option.value === value}
          class:highlighted={index === highlightedIndex}
          aria-selected={option.value === value}
          on:click={() => selectOption(option)}
          on:mouseenter={() => (highlightedIndex = index)}
        >
          <span class="select-field-option-label">{option.label}</span>
          {#if option.description}
            <span class="select-field-option-description">{option.description}</span>
          {/if}
        </button>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .select-field {
    position: relative;
    width: 100%;
  }

  .select-field-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background-color: white;
    font-size: 0.875rem;
    font-family: inherit;
    color: #374151;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .select-field-trigger.placeholder .select-field-value {
    color: rgba(0, 0, 0, 0.45);
  }

  .select-field-trigger:hover:not(:disabled) {
    border-color: #9ca3af;
  }

  .select-field-trigger:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.15);
  }

  .select-field-trigger.open {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.15);
  }

  .select-field-trigger.invalid {
    border-color: var(--color-negative);
    box-shadow: 0 0 0 1px rgba(252, 108, 107, 0.15);
  }

  .select-field-trigger.invalid:focus,
  .select-field-trigger.invalid.open {
    border-color: var(--color-negative);
    box-shadow: 0 0 0 3px rgba(252, 108, 107, 0.2);
  }

  .select-field-trigger:disabled {
    background-color: #f3f4f6;
    color: black;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .select-field-value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .select-field-chevron {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    transition: transform 0.2s ease, color 0.2s ease;
  }

  .select-field-chevron.open {
    transform: rotate(180deg);
    color: var(--color-primary);
  }

  :global(ul.select-field-options) {
    margin: 0;
    padding: 0.375rem;
    list-style: none;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.08),
      0 4px 10px rgba(0, 0, 0, 0.04);
    overflow-y: auto;
    box-sizing: border-box;
  }

  :global(ul.select-field-options .select-field-option) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
    width: 100%;
    padding: 0.75rem 0.875rem;
    border: none;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-family: inherit;
  }

  :global(ul.select-field-options .select-field-option:hover),
  :global(ul.select-field-options .select-field-option.highlighted) {
    background-color: #f9fafb;
  }

  :global(ul.select-field-options .select-field-option.selected) {
    background-color: rgba(255, 180, 0, 0.12);
  }

  :global(ul.select-field-options .select-field-option.selected .select-field-option-label) {
    color: #92400e;
    font-weight: 600;
  }

  :global(ul.select-field-options .select-field-option:focus) {
    outline: none;
    background-color: #f3f4f6;
  }

  :global(ul.select-field-options .select-field-option-label) {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    line-height: 1.4;
  }

  :global(ul.select-field-options .select-field-option-description) {
    font-size: 0.75rem;
    color: #6b7280;
    line-height: 1.4;
  }

  :global(ul.select-field-options::-webkit-scrollbar) {
    width: 6px;
  }

  :global(ul.select-field-options::-webkit-scrollbar-track) {
    background: transparent;
  }

  :global(ul.select-field-options::-webkit-scrollbar-thumb) {
    background: #d1d5db;
    border-radius: 9999px;
  }
</style>
